import { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OrganizationEntry, getOrganizations } from '../../api/organizationsApi';
import { ChevronRightIcon } from '../../components/icons';
import CreateOrganizationModal from '../../components/organizations/CreateOrganizationModal';
import { useToast } from '../../context/ToastContext';

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
}

export default function ChooseOrganizationView() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [organizations, setOrganizations] = useState<OrganizationEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    getOrganizations()
      .then((res) => setOrganizations(res.rows))
      .finally(() => setLoading(false));
  }, []);

  function handleOrganizationCreated(name: string) {
    showToast(t('addOrganization.successToast', { name }));
    getOrganizations().then((res) => setOrganizations(res.rows));
  }

  return (
    <Box sx={(theme) => ({ p: 4, width: '100%', flex: 1, backgroundColor: theme.app.mutedBackground })}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="h4">{t('organizations.title')}</Typography>
          {!loading && (
            <Typography variant="h4" color="text.secondary">
              {organizations.length}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          disableElevation
          onClick={() => setModalOpen(true)}
          sx={(theme) => ({
            backgroundColor: theme.app.primary,
            color: theme.app.primaryForeground,
            '&:hover': { backgroundColor: theme.app.primaryHover },
          })}
        >
          {t('organizations.createButton')}
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : organizations.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('organizations.noOrganizations')}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {organizations.map((organization) => (
            <OrganizationRow key={organization.id} organization={organization} />
          ))}
        </Box>
      )}

      <CreateOrganizationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={handleOrganizationCreated}
      />
    </Box>
  );
}

function OrganizationRow({ organization }: { organization: OrganizationEntry }) {
  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        px: 3,
        py: 2,
        borderRadius: 2,
        border: `1px solid ${theme.app.border}`,
        backgroundColor: theme.app.card,
      })}
    >
      <Box
        sx={(theme) => ({
          width: 44,
          height: 44,
          borderRadius: '50%',
          backgroundColor: theme.app.accentSurface,
          color: theme.app.accent,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          mr: 2,
        })}
      >
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {getInitials(organization.name)}
        </Typography>
      </Box>

      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {organization.name}
        </Typography>
      </Box>

      <Box
        sx={(theme) => ({
          width: 32,
          height: 32,
          borderRadius: '50%',
          backgroundColor: theme.app.secondary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          ml: 1,
          color: theme.app.mutedForeground,
        })}
      >
        <ChevronRightIcon size={16} />
      </Box>
    </Box>
  );
}
